import jsPDF from "jspdf";

export function generateAgroGuardReport() {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

  const pageW = 210;
  const pageH = 297;
  const margin = 18;
  const contentW = pageW - margin * 2;
  let y = 0;

  // ── helpers ──────────────────────────────────────────────────────────────
  const GREEN = [34, 139, 34] as [number, number, number];
  const DARK = [30, 30, 30] as [number, number, number];
  const GRAY = [100, 100, 100] as [number, number, number];
  const LIGHT_GREEN = [240, 255, 240] as [number, number, number];
  const LIGHT_GRAY = [248, 248, 248] as [number, number, number];
  const WHITE = [255, 255, 255] as [number, number, number];

  function checkPage(needed = 20) {
    if (y + needed > pageH - 20) {
      doc.addPage();
      y = 20;
    }
  }

  function sectionTitle(text: string) {
    checkPage(18);
    y += 6;
    doc.setFillColor(...GREEN);
    doc.roundedRect(margin, y, contentW, 10, 2, 2, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...WHITE);
    doc.text(text, margin + 4, y + 7);
    y += 14;
    doc.setTextColor(...DARK);
  }

  function subTitle(text: string) {
    checkPage(12);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...GREEN);
    doc.text(text, margin, y);
    y += 6;
    doc.setTextColor(...DARK);
  }

  function bodyText(text: string, indent = 0) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...DARK);
    const lines = doc.splitTextToSize(text, contentW - indent);
    lines.forEach((line: string) => {
      checkPage(6);
      doc.text(line, margin + indent, y);
      y += 5;
    });
  }

  function bullet(text: string) {
    checkPage(6);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...DARK);
    doc.setFillColor(...GREEN);
    doc.circle(margin + 3, y - 1.2, 1, "F");
    const lines = doc.splitTextToSize(text, contentW - 8);
    lines.forEach((line: string, i: number) => {
      doc.text(line, margin + 7, y + i * 5);
    });
    y += lines.length * 5;
  }

  function tableRow(
    cols: string[],
    widths: number[],
    isHeader = false,
    shade = false
  ) {
    checkPage(8);
    const rowH = 8;
    let x = margin;
    if (isHeader) {
      doc.setFillColor(...GREEN);
    } else if (shade) {
      doc.setFillColor(...LIGHT_GREEN);
    } else {
      doc.setFillColor(...LIGHT_GRAY);
    }
    doc.rect(margin, y, contentW, rowH, "F");
    doc.setDrawColor(200, 200, 200);
    doc.rect(margin, y, contentW, rowH, "S");

    cols.forEach((col, i) => {
      doc.setFont("helvetica", isHeader ? "bold" : "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(isHeader ? 255 : 30, isHeader ? 255 : 30, isHeader ? 255 : 30);
      const cellText = doc.splitTextToSize(col, widths[i] - 3);
      doc.text(cellText[0] ?? "", x + 2, y + 5.5);
      x += widths[i];
    });
    y += rowH;
  }

  function divider() {
    checkPage(4);
    doc.setDrawColor(...GREEN);
    doc.setLineWidth(0.3);
    doc.line(margin, y, margin + contentW, y);
    y += 4;
  }

  // ── COVER PAGE ────────────────────────────────────────────────────────────
  doc.setFillColor(...GREEN);
  doc.rect(0, 0, pageW, 60, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(28);
  doc.setTextColor(...WHITE);
  doc.text("AgroGuard", pageW / 2, 28, { align: "center" });

  doc.setFontSize(13);
  doc.setFont("helvetica", "normal");
  doc.text("Functional Requirements & Stakeholder Report", pageW / 2, 40, {
    align: "center",
  });

  doc.setFontSize(10);
  doc.text(
    `Generated: ${new Date().toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    })}`,
    pageW / 2,
    52,
    { align: "center" }
  );

  // tagline box
  doc.setFillColor(...LIGHT_GREEN);
  doc.roundedRect(margin, 68, contentW, 18, 3, 3, "F");
  doc.setFont("helvetica", "italic");
  doc.setFontSize(10);
  doc.setTextColor(...GREEN);
  doc.text(
    "AI-Powered Crop Disease Detection & Smart Farming Assistant",
    pageW / 2,
    79,
    { align: "center" }
  );

  y = 96;

  // ── EXECUTIVE SUMMARY ─────────────────────────────────────────────────────
  sectionTitle("Executive Summary");
  bodyText(
    "AgroGuard is a Next.js + Supabase web application that empowers farmers with AI-driven crop disease detection, " + "treatment guidance, and proactive email notifications. The platform serves two primary human stakeholders — Farmers "+ "and Admins — supported by four technical stakeholders: Gemini AI, Resend, Supabase, and MobileNet. "+ "This document outlines all functional requirements and the full stakeholder relationship model."
  );

  // ── FUNCTIONAL REQUIREMENTS ───────────────────────────────────────────────
  sectionTitle("1. Functional Requirements");

  const modules = [
    {
      title: "1.1  Authentication & User Management",
      items: [
        "User registration and login (email/password)",
        "Password reset and forgot password flows",
        "OAuth callback handling",
        "Role-based access control (Farmer vs Admin)",
        "Admin user management — view and manage all users",
      ],
    },
    {
      title: "1.2  Crop Disease Analysis",
      items: [
        "Upload crop images for AI-powered disease detection",
        "Gemini AI integration for cloud-based crop analysis",
        "MobileNet on-device analysis as fallback/supplement",
        "Crop type selection before analysis",
        "Display results: disease name, confidence score, severity",
        "Prevention tips panel based on detected disease",
        "Analysis history per farmer",
      ],
    },
    {
      title: "1.3  Treatment & Feedback Tracking",
      items: [
        "Farmers submit treatment feedback after applying recommendations",
        "Track treatment success/failure outcomes",
        "Analysis feedback — rating and comment on AI accuracy",
        "Treatment success data stored in Supabase",
      ],
    },
    {
      title: "1.4  AI Chat Assistant",
      items: [
        "Conversational AI chatbot powered by Gemini",
        "Multi-turn chat with history persistence",
        "Farming-specific Q&A support",
      ],
    },
    {
      title: "1.5  Farmer Dashboard",
      items: [
        "KPI cards: total analyses, diseases found, treatments applied",
        "Disease distribution chart",
        "Analysis history table",
        "Activity feed",
        "Analysis trend chart",
      ],
    },
    {
      title: "1.6  Admin Dashboard",
      items: [
        "KPI overview: total farmers, analyses, active outbreaks",
        "Analysis volume chart over time",
        "Disease frequency bar chart",
        "Farmer growth chart",
        "Treatment success rate visualization",
        "Disease location map",
        "Feedback statistics",
        "Audit logs table",
        "System health monitoring",
        "Data export section",
      ],
    },
    {
      title: "1.7  Notifications System",
      items: [
        "In-app notifications for disease alerts, treatment reminders, analysis updates",
        "Notification settings per farmer (email, SMS, in-app)",
        "Notification type preferences: outbreaks, treatment, analysis",
      ],
    },
    {
      title: "1.8  Email Notifications (Resend + Supabase Edge Function)",
      items: [
        "Weekly farming tips emails based on crop type",
        "Seasonal planting reminders (Kharif / Rabi seasons)",
        "Disease prevention alerts for unread outbreaks",
        "Email logs tracked in database (sent/failed status)",
        "Respects farmer's notification preferences",
      ],
    },
    {
      title: "1.9  Farming Guide",
      items: [
        "Curated farming guides and planting calendar",
        "Crop-specific disease prevention tips",
        "Seasonal farming recommendations",
      ],
    },
    {
      title: "1.10  Farmer Profile",
      items: [
        "View and edit personal profile",
        "Crop type preferences",
        "Contact method preferences",
      ],
    },
    {
      title: "1.11  Settings & Preferences",
      items: [
        "Notification preferences management",
        "Language / display settings",
        "Account settings",
      ],
    },
    {
      title: "1.12  About Page",
      items: [
        "Mission and vision section",
        "Team section",
        "Impact statistics",
        "AI technology overview",
        "Partner CTA",
      ],
    },
    {
      title: "1.13  Public Landing (Home Page)",
      items: [
        "Hero section with CTA",
        "Features overview",
        "How it works",
        "Supported crops",
        "Testimonials",
        "Stats bar",
        "Footer",
      ],
    },
  ];

  modules.forEach((mod) => {
    subTitle(mod.title);
    mod.items.forEach((item) => bullet(item));
    y += 2;
  });

  // ── STAKEHOLDERS ──────────────────────────────────────────────────────────
  sectionTitle("2. Stakeholders");

  subTitle("2.1  Primary Stakeholders");
  y += 1;
  const primaryCols = ["Stakeholder", "Role", "Description"];
  const primaryWidths = [38, 30, contentW - 68];
  tableRow(primaryCols, primaryWidths, true);
  [
    ["Farmer", "End User", "Uploads crop images, receives disease analysis, follows treatment plans, gets email/in-app alerts"],
    ["Admin", "Platform Manager", "Monitors platform health, manages users, views analytics, exports data, oversees disease outbreak trends"],
    ["AgroGuard System", "Platform", "The Next.js + Supabase application that orchestrates all interactions"],
  ].forEach(([a, b, c], i) => tableRow([a, b, c], primaryWidths, false, i % 2 === 0));

  y += 6;
  subTitle("2.2  Supporting / Technical Stakeholders");
  y += 1;
  const suppCols = ["Stakeholder", "Role", "Description"];
  const suppWidths = [38, 30, contentW - 68];
  tableRow(suppCols, suppWidths, true);
  [
    ["Gemini AI", "AI Provider", "Powers crop disease detection from images and the AI chat assistant"],
    ["Resend", "Email Service", "Delivers weekly tips, seasonal reminders, and disease alerts to farmers"],
    ["Supabase", "Backend / DB", "Stores all user data, analyses, notifications, logs; hosts Edge Functions"],
    ["MobileNet", "On-device AI", "Provides lightweight, client-side crop analysis as a fallback/supplement"],
  ].forEach(([a, b, c], i) => tableRow([a, b, c], suppWidths, false, i % 2 === 0));

  // ── STAKEHOLDER RELATIONSHIPS ─────────────────────────────────────────────
  sectionTitle("3. Stakeholder Relationships");

  const relationships = [
    {
      actor: "Farmer",
      relations: [
        "Uploads crop images → AgroGuard System",
        "Receives analysis results ← Gemini AI (via System)",
        "Chats with AI assistant ← Gemini AI (via System)",
        "Receives email alerts ← Resend (triggered by Supabase Edge Function)",
        "Manages notification preferences → Supabase (stored)",
        "Submits treatment feedback → Supabase (stored)",
      ],
    },
    {
      actor: "Admin",
      relations: [
        "Monitors all farmer activity ← Supabase (read)",
        "Manages user accounts → Supabase (write)",
        "Views disease outbreak trends ← Supabase (aggregated)",
        "Exports reports ← AgroGuard System",
      ],
    },
    {
      actor: "AgroGuard System",
      relations: [
        "Authenticates users via → Supabase Auth",
        "Stores analyses, feedback, logs → Supabase DB",
        "Sends crop image to → Gemini AI API",
        "Triggers email sends via → Supabase Edge Function → Resend",
        "Serves UI to → Farmer & Admin",
      ],
    },
    {
      actor: "Supabase Edge Function (send-farmer-emails)",
      relations: [
        "Queries eligible farmers ← Supabase DB",
        "Fetches crop-specific tips ← Supabase DB (farming_guide, disease_prevention_tips)",
        "Sends emails via → Resend API",
      ],
    },
  ];

  relationships.forEach((r) => {
    subTitle(r.actor);
    r.relations.forEach((rel) => bullet(rel));
    y += 2;
  });

  // ── DATA FLOW SUMMARY ─────────────────────────────────────────────────────
  sectionTitle("4. Data Flow Summary");

  const flowRows = [
    ["Image Upload", "Farmer → AgroGuard → Gemini AI", "Disease name, confidence, severity, tips"],
    ["Chat Query", "Farmer → AgroGuard → Gemini AI", "Conversational farming advice"],
    ["Treatment Feedback", "Farmer → AgroGuard → Supabase DB", "Outcome stored for analytics"],
    ["Email Notification", "Supabase Edge Fn → Resend → Farmer", "Weekly tips / alerts / reminders"],
    ["Admin Analytics", "Supabase DB → AgroGuard → Admin", "Charts, KPIs, audit logs"],
    ["User Auth", "Farmer/Admin → Supabase Auth → AgroGuard", "JWT session token"],
  ];

  const flowCols = ["Flow Name", "Path", "Output"];
  const flowWidths = [38, 80, contentW - 118];
  tableRow(flowCols, flowWidths, true);
  flowRows.forEach(([a, b, c], i) => tableRow([a, b, c], flowWidths, false, i % 2 === 0));

  // ── FOOTER on every page ──────────────────────────────────────────────────
  const totalPages = (doc as any).internal.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    doc.setFillColor(...GREEN);
    doc.rect(0, pageH - 12, pageW, 12, "F");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...WHITE);
    doc.text("AgroGuard — Confidential Project Report", margin, pageH - 4);
    doc.text(`Page ${p} of ${totalPages}`, pageW - margin, pageH - 4, {
      align: "right",
    });
  }

  doc.save("AgroGuard_Requirements_Report.pdf");
}
